import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Button } from 'js/design_system/Button';
import { Body } from 'js/design_system/Body';
import { Icon } from 'js/design_system/Icon';
import { Spinner } from 'js/design_system/Spinner';
import { BodyLink } from 'js/design_system/link/BodyLink';
import {
  getBulkDownloadFileList,
  getBulkDownloadServerSideZipUrl,
  BulkDownloadFilesResponse,
} from 'js/bulk_download/bulk_download_helper';
import { recordBulkDownload } from 'js/bulk_download/record_bulk_download';
import { ImpressionWatcher } from 'js/utils/impressions/ImpressionWatcher';
import { ImpressionEntity } from 'js/utils/impressions/watchImpressions';
import { createTopic } from '../api/researchRadarApi';
import {
  trackIntermediateTrackClicked,
  trackIntermediateDismissed,
  trackTopicCreated,
  IMPRESSION_SOURCES,
} from '../tracking';
import { TrackingBadge } from '../components/TrackingBadge';
import routes from 'js/core_pre_webpack/routes';
import { CATEGORY_LABELS, DATE_RANGE_LABELS, type DateRange } from '../types';

const STORAGE_KEY = 'research_radar.intermediate_state';

export interface IntermediateScreenState {
  sourceWorkId: number;
  sourceWorkTitle: string;
  sourceWorkSlug: string;
  downloadWorkIds: number[];
  workBuckets: Record<number, string>;
  topicQuery: string;
  dateRange: string;
  selectedBuckets: string[];
}

export function storeIntermediateState(state: IntermediateScreenState): void {
  if (typeof window === 'undefined') return;
  try {
    window.sessionStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // sessionStorage unavailable
  }
}

function readIntermediateState(): IntermediateScreenState | null {
  if (typeof window === 'undefined') return null;
  try {
    const raw = window.sessionStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as IntermediateScreenState) : null;
  } catch {
    return null;
  }
}

function clearIntermediateState(): void {
  try {
    window.sessionStorage.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

type DownloadStage = 'loading' | 'zipping' | 'success' | 'error';

const BLANK_PAPER_URL = '/images/blank-paper.jpg';

const FEATURE_BULLETS = [
  { icon: 'mail', text: 'Email digest' },
  { icon: 'auto_awesome', text: 'Auto-updated weekly' },
  { icon: 'download', text: 'Unlimited PDF download' },
];

interface Props {
  onTopicCreated: (topicId: number) => void;
}

const IntermediateScreen: React.FC<Props> = ({ onTopicCreated }) => {
  const [state] = useState(readIntermediateState);
  const [downloadStage, setDownloadStage] = useState<DownloadStage>('loading');
  const [progress, setProgress] = useState(0);
  const [zipUrl, setZipUrl] = useState<string | null>(null);
  const [tracking, setTracking] = useState(false);
  const [tracked, setTracked] = useState(false);
  const [thumbnailUrls, setThumbnailUrls] = useState<string[]>([]);
  const linkRef = useRef<HTMLAnchorElement>(null);
  const intervalRef = useRef<number | null>(null);
  const trackedRef = useRef(false);

  useEffect(() => {
    if (!state) return;
    clearIntermediateState();
    const sourceWorkId = state.sourceWorkId;
    return () => {
      if (!trackedRef.current) {
        trackIntermediateDismissed(sourceWorkId);
      }
    };
  }, [state]);

  const startFakeProgress = useCallback((fileCount: number) => {
    intervalRef.current = window.setInterval(() => {
      setProgress((prev) => {
        const increment = 95 / ((fileCount + 1) * 4);
        const next = prev + increment;
        return next < 95 ? next : 95;
      });
    }, 334);
  }, []);

  useEffect(() => {
    if (!state) return;

    const { sourceWorkId, downloadWorkIds, workBuckets } = state;
    startFakeProgress(downloadWorkIds.length);

    getBulkDownloadFileList(
      sourceWorkId.toString(),
      [],
      'server-side',
      downloadWorkIds,
      workBuckets,
    )
      .then((fileList: BulkDownloadFilesResponse) => {
        setDownloadStage('zipping');
        if (fileList.thumbnailUrls?.length) {
          setThumbnailUrls(fileList.thumbnailUrls);
        }
        const slug = state.sourceWorkSlug || `work-${sourceWorkId}`;
        return getBulkDownloadServerSideZipUrl(
          fileList.files,
          slug,
          fileList.zipperUrl,
        ).then((url) => ({ url, files: fileList.files }));
      })
      .then(({ url, files }) => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setProgress(100);
        setDownloadStage('success');
        setZipUrl(url);
        recordBulkDownload({
          work_id: state.sourceWorkId.toString(),
          files,
        });
      })
      .catch(() => {
        if (intervalRef.current) clearInterval(intervalRef.current);
        setDownloadStage('error');
      });

    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, [state, startFakeProgress]);

  useEffect(() => {
    if (zipUrl && linkRef.current) {
      linkRef.current.click();
    }
  }, [zipUrl]);

  const handleTrackTopic = useCallback(async () => {
    if (!state || tracking) return;
    setTracking(true);

    try {
      trackIntermediateTrackClicked(state.sourceWorkId);
      const response = await createTopic({
        seed_work_id: state.sourceWorkId,
        freeform_query: state.topicQuery || undefined,
        date_range: (state.dateRange as DateRange) || undefined,
        categories:
          state.selectedBuckets.length > 0 ? state.selectedBuckets : undefined,
      });
      const topicId = response.topic.id;
      trackTopicCreated(topicId, 'intermediate');
      trackedRef.current = true;
      setTracked(true);
      onTopicCreated(topicId);
    } catch {
      setTracking(false);
    }
  }, [state, tracking, onTopicCreated]);

  if (!state) {
    return (
      <div className="radar-intermediate">
        <div className="radar-intermediate__empty">
          <Body>No download in progress.</Body>
          <Button
            size="sm"
            secondary
            onClick={() => {
              if (typeof window !== 'undefined') {
                window.location.href = routes.research_radar_path();
              }
            }}
          >
            Go to Radar
          </Button>
        </div>
      </div>
    );
  }

  const isComplete = downloadStage === 'success';
  const bundleSlug = state.sourceWorkSlug || `work-${state.sourceWorkId}`;

  const filterSummary: string[] = [];
  if (state.dateRange && state.dateRange !== 'all') {
    const label =
      DATE_RANGE_LABELS[state.dateRange as DateRange] ?? state.dateRange;
    filterSummary.push(label);
  }
  state.selectedBuckets.forEach((b) => {
    const label = CATEGORY_LABELS[b] ?? b;
    filterSummary.push(label);
  });

  return (
    <ImpressionWatcher
      source={IMPRESSION_SOURCES.intermediate}
      entity_id={state.sourceWorkId}
      entity_type={ImpressionEntity.Work}
      className="radar-intermediate"
    >
      <div
        className={`radar-intermediate__status-card${
          isComplete ? ' radar-intermediate__status-card--success' : ''
        }`}
      >
        <div className="radar-intermediate__status-icon">
          <Icon name={isComplete ? 'check' : 'download'} size={40} />
        </div>
        <div className="radar-intermediate__status-content">
          <Body size="md" className="radar-intermediate__status-title">
            {isComplete
              ? 'Your download is complete!'
              : 'Preparing your PDF package...'}
          </Body>
          <div className="radar-intermediate__progress-row">
            <div className="radar-intermediate__progress-bar">
              <div
                className={`radar-intermediate__progress-fill${
                  isComplete
                    ? ' radar-intermediate__progress-fill--success'
                    : ''
                }`}
                style={{ width: `${progress}%` }}
              />
            </div>
            <Body size="sm" className="radar-intermediate__progress-pct">
              {Math.round(progress)}%
            </Body>
          </div>
        </div>
      </div>

      {downloadStage === 'error' && (
        <Body size="sm" className="radar-intermediate__error">
          Something went wrong. Please try again.
        </Body>
      )}
      {isComplete && zipUrl && (
        <Body size="sm" className="radar-intermediate__download-link">
          Your download should start automatically.{' '}
          <BodyLink
            ref={linkRef}
            href={zipUrl}
            download={`Academia.edu Bundle - ${bundleSlug}.zip`}
          >
            Click here
          </BodyLink>{' '}
          if it doesn&apos;t.
        </Body>
      )}

      {/* Combined promo + topic card */}
      <div className="radar-intermediate__card">
        {/* Promo top section with thumbnails */}
        <div className="radar-intermediate__promo-section">
          <div className="radar-intermediate__promo-thumbnails">
            <div className="radar-intermediate__thumb radar-intermediate__thumb--back">
              <img
                src={thumbnailUrls[1] || BLANK_PAPER_URL}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).src = BLANK_PAPER_URL;
                }}
              />
            </div>
            <div className="radar-intermediate__thumb radar-intermediate__thumb--front">
              <img
                src={thumbnailUrls[0] || BLANK_PAPER_URL}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).src = BLANK_PAPER_URL;
                }}
              />
            </div>
            <div className="radar-intermediate__thumb radar-intermediate__thumb--back radar-intermediate__thumb--right">
              <img
                src={thumbnailUrls[2] || BLANK_PAPER_URL}
                alt=""
                onError={(e) => {
                  (e.target as HTMLImageElement).src = BLANK_PAPER_URL;
                }}
              />
            </div>
          </div>
          <div className="radar-intermediate__promo-content">
            <div className="radar-intermediate__promo-text">
              <Body size="md" className="radar-intermediate__promo-title">
                Track this topic. Never miss new research.
              </Body>
              <Body size="sm" className="radar-intermediate__promo-subtitle">
                We find them automatically. You just read them.
              </Body>
            </div>
            <div className="radar-intermediate__feature-list">
              {FEATURE_BULLETS.map(({ icon, text }) => (
                <span key={icon} className="radar-intermediate__feature-item">
                  <Icon name={icon} size={16} />
                  <Body size="xs">{text}</Body>
                </span>
              ))}
            </div>
          </div>
        </div>

        {/* Topic details bottom section */}
        <div className="radar-intermediate__topic-section">
          <div className="radar-intermediate__topic-info">
            <div className="radar-intermediate__topic-badges">
              <TrackingBadge active={tracked} />
            </div>
            <div className="radar-intermediate__topic-title">
              {state.topicQuery || state.sourceWorkTitle}
            </div>
            <div className="radar-source-papers">
              <span className="radar-source-papers__label">Source papers:</span>
              <div className="radar-source-papers__chips">
                <span className="radar-chip">
                  <span className="radar-chip__text">
                    {state.sourceWorkTitle}
                  </span>
                </span>
                {filterSummary.length > 0 && (
                  <span className="radar-chip">
                    <span className="radar-chip__text">
                      + {filterSummary.length} filter
                      {filterSummary.length !== 1 ? 's' : ''}
                    </span>
                  </span>
                )}
              </div>
            </div>
          </div>
          <div className="radar-intermediate__topic-actions">
            <Button
              size="sm"
              secondary
              icon="edit"
              onClick={handleTrackTopic}
            />
            {tracked ? (
              <Button size="sm" disabled icon="check">
                Topic tracked
              </Button>
            ) : (
              <Button size="sm" onClick={handleTrackTopic} disabled={tracking}>
                {tracking ? <Spinner size={16} /> : 'Track topic'}
              </Button>
            )}
          </div>
        </div>
      </div>
    </ImpressionWatcher>
  );
};

export default IntermediateScreen;
